import {
  useMedicareQuote,
  useMedigapQuote,
  usePdpQuote,
  useSaveMedicareQuote,
  useSaveUserProfile,
  useUserProfile,
} from '.';
import { MedicareQuoteFilterExtraBenefit } from '@coverright/data-access/types/medicare';
import { useSavePdpQuoteMutation } from '@coverright/data-access/medicare';
import { useSaveMedigapQuoteMutation } from '@coverright/data-access/types/medigap';

export const useChangeZip = () => {
  const [saveProfile] = useSaveUserProfile();
  const profile = useUserProfile();

  const [saveMedicareQuote] = useSaveMedicareQuote();
  const medicareQuote = useMedicareQuote();

  const [saveMedigapQuote] = useSaveMedigapQuoteMutation({
    refetchQueries: ['medigapQuote'],
  });
  const medigapQuote = useMedigapQuote();

  const [savePdpQuote] = useSavePdpQuoteMutation({
    refetchQueries: ['pdpQuote'],
  });
  const pdpQuote = usePdpQuote();

  return (zip: string, county: string, state: string) => {
    if (profile) {
      saveProfile({
        variables: {
          data: {
            zip,
            countyName: county,
            state,
            profileId: profile.profileId,
          },
        },
        optimisticResponse: (vars) => {
          return {
            saveUserProfile: {
              enrollmentId: null,
              profileId: profile.profileId,
              userId: null,
              userToken: null,
              __typename: 'UserProfileSaveOutput',
            },
          };
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify(profile),
            fields: {
              zip: () => zip,
              countyName: () => county,
              state: () => state,
            },
          });
        },
      });
    }



    if (medicareQuote) {
      const medicareFilters = {
        maxOutOfPocketRangeNames: [],
        maxCostRangeNames: [],
        rating: [],
        planTypes: [],
        SNPTypes: [],
        extraBenefits: [MedicareQuoteFilterExtraBenefit.DrugCoverage],
        companies: [],
        showTopDoctors: false,
        showTopDrugs: false,
        partBPremiumReduction: false,
      };
      saveMedicareQuote({
        variables: {
          data: {
            zip, county,
            filters: medicareFilters,
            id: medicareQuote.id },
        },
        optimisticResponse: (vars) => {
          return {
            saveMedicareQuote: {
              __typename: 'SaveMedicareQuoteOutput',
              maQuoteID: medicareQuote.id,
              clientId: medicareQuote.clientId,
            },
          };
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify(medicareQuote),
            fields: {
              filters: () => ({
                '__typename': 'FiltersDto',
                ...medicareFilters
              }),
              zip: () => zip,
              county: () => county,
            },
          });
        },
      });
    }

    if (medigapQuote) {
      saveMedigapQuote({
        variables: {
          input: {
            id: medigapQuote.id,
            clientId: medigapQuote.clientId,
            cId: medigapQuote.cId,
            medigapFilterState: {
              age: medigapQuote.medigapFilterState?.age,
              tobacco: medigapQuote.medigapFilterState?.tobacco,
              effectiveDate: medigapQuote.medigapFilterState?.effectiveDate,
              gender: medigapQuote.medigapFilterState?.gender,
              zip,
              county,
            },
          },
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify(medigapQuote),
            fields: {
              medigapFilterState: (cachedState) => ({
                ...cachedState,
                zip,
                county,
              }),
            },
          });
        },
      });
    }

    if (pdpQuote) {
      savePdpQuote({
        variables: {
          input: {
            id: pdpQuote.id,
            zip,
            countyName: county,
            cId: pdpQuote.cId,
            insulinSavings: pdpQuote.insulinSavings,
          },
        },
        optimisticResponse: (vars) => {
          return {
            savePdpQuote: {
              ...pdpQuote,
              zip,
              countyName: county,
            },
          };
        },
      });
    }
  };
};
